<script>
    import R, {LOADER} from "../lib/requests";
    import user from "../stores/user";
    import shift$ from "../stores/shift$";
    import Alert from "../stores/alerts";
    import Notifier from "../stores/notifier";
    import {formatDate, millisecondsToHours, toInt} from "../lib/lib";
    import PageHeader from "../components/PageHeader.svelte";
    import DriverSelectControl from "../components/controls/DriverSelectControl.svelte";
    import DateControl from "../components/controls/DateControl.svelte";
    import ShiftModal from "./ShiftModal.svelte";
    import TableHeader from "../components/TableHeader.svelte";
    import AppIcon from "../components/icons/AppIcon.svelte";
    import Pagination from "../components/Pagination.svelte";
    import VehicleControl from "../components/controls/VehicleControl.svelte";
    import ClockingShift from "./ClockingShift.svelte";
    import ShiftsReport from "./ShiftsReport.svelte";

    const itemsPerPage = [50, 100, 500, 1000];

    let pageHeaderControls = [
        {id: 'create', name: 'NEW SHIFT', class: 'hidden-xs'},
        {
            id: 'create',
            name: '',
            icon: 'plus-circle',
            class: 'hidden-xl hidden-lg hidden-md hidden-sm'
        },
        {id: 'open_report', name: 'Report', icon: 'file-excel', is_hidden: !user.isGod()},
        {id: 'close_report', name: 'List', icon: 'list', is_hidden: true},
        {id: 'reload', name: '', icon: 'refresh'}
    ];

    const tableColumns = [
        {key: 'start_at', title: 'START', sortable: true, sort_way: 'ASC'},
        {key: 'end_at', title: 'END', sortable: true, class: 'hidden-sm hidden-xs'},
        {key: 'duration', title: 'DURATION', sortable: true, class: 'hidden-sm hidden-xs'},
        {key: 'total_klm', title: 'KLM', sortable: true, class: 'hidden-sm hidden-xs'},
        {key: 'total_expenses', title: 'EXP.', sortable: true, class: 'hidden-sm hidden-xs', hidden: !user.isGod()},
        {key: 'total_incomes', title: 'INC.', sortable: true, class: 'hidden-sm hidden-xs', hidden: !user.isGod()},
        {key: 'driver', title: 'DRIVER', sortable: false, class: 'hidden-sm hidden-xs'},
        {key: 'vehicle', title: 'VEHICLE', sortable: false, class: 'hidden-sm hidden-xs'},
        {key: '', title: ''},
    ];

    let shifts = null;
    let totalShifts = 0;
    let totalKlm = 0;
    let totalExpenses = 0;
    let totalIncomes = 0;
    let search = {
        limit: itemsPerPage[0],
        offset: 0,
        date_from: null,
        date_to: null,
        driver_id: null,
        vehicle_id: null,
    };
    let page = 1;
    let selectedId = null;
    let editId = null;
    let showReport = false;

    const load = () => {
        const onSuccess = result => {
            shifts = result.shifts;
            totalShifts = result.totals.total_shifts;
            totalKlm = result.totals.total_klm;
            totalExpenses = result.totals.total_expenses;
            totalIncomes = result.totals.total_incomes;
        }

        const onFailure = err => {
        };

        search.offset = (page - 1) * search.limit;

        R.handle(R.shift.index, search, onSuccess, onFailure, (shifts == null ? LOADER.PAGE : LOADER.POPUP));
    }

    const onSearch = () => {
        page = 1;
        load();
    }

    const onColumnSort = ({detail}) => {
        search.sort_field = detail.key;
        search.sort_way = detail.sort_way;
        page = 1;
        load();
    }

    const onPageChange = ({detail}) => {
        page = detail;
        load();
    }

    const clearSearch = () => {
        search.date_from = null;
        search.date_to = null;
        search.driver_id = null;
        search.vehicle_id = null;

        onSearch();
    }

    const createShift = () => {
        if ($shift$.id > 0) {
            Alert.warning("Shift", "You have already a clocking shift!");
            return;
        }

        editId = 0;
    }

    const editShift = shift => {
        editId = shift.id;
    }

    const closeShift = (param) => {
        editId = null;
        if (param) {
            if (param.id == $shift$.id) {
                shift$.clear();
            }
            load();
        }
    }

    const deleteShift = shift => {
        const onSuccess = result => {
            shifts = shifts.filter(x => x.id != shift.id);
            Notifier.showMessage("Shift deleted");
        }

        const onFailure = err => {
        };

        Alert.delete("Delete", "Are you sure you want to delete this shift?", () => {
            R.handle(R.shift.delete, shift.id, onSuccess, onFailure, LOADER.POPUP);
        });
    }


    const setPageHeaderControls = () => {
        pageHeaderControls.map(item => {
            if (item.id === 'open_report' || item.id === 'create' || item.id === 'reload') {
                item.is_hidden = showReport;
            } else if (item.id === 'close_report') {
                item.is_hidden = !showReport;
            }
        });
        pageHeaderControls = pageHeaderControls;
    }

    const openReport = () => {
        if (totalShifts > 100 && (!search.date_from || !search.date_to)) {
            Notifier.showError("Specify the date period for shift reports");
            return;
        }
        showReport = true;
        setPageHeaderControls();
    }

    const closeReport = () => {
        showReport = false;
        setPageHeaderControls();
    }

    load();
</script>

<PageHeader title="Shifts" controls={ pageHeaderControls }
            on:create={createShift}
            on:open_report={openReport}
            on:close_report={closeReport}
            on:reload={ load }/>

{#if showReport}
    <ShiftsReport {search} />
{:else}
    <ClockingShift openClockingShift={editShift}/>

    <div class="panel-simple p-2 mb-5">
        <div class="row">
            <div class="col-xl-2 col-6">
                <DateControl placeholder="from" bind:value="{ search.date_from }"
                             on:change="{ onSearch }"/>
            </div>
            <div class="col-xl-2 col-6 form-group-xs">
                <DateControl placeholder="to" bind:value="{ search.date_to }" openRight
                             on:change="{ onSearch }"/>
            </div>
            <div class="col-xl-2">
                {#if user.isGod()}
                    <DriverSelectControl bind:driver_id={search.driver_id} placeholder="driver" on:change={onSearch}/>
                {/if}
            </div>
            <div class="col-xl-2">
                {#if user.isGod()}
                    <VehicleControl bind:vehicle_id={search.vehicle_id} on:change={onSearch} placeholder="vehicle"/>
                {/if}
            </div>
            <div class="col-xl-4 text-end">
                <button class="btn btn-default" on:click="{ clearSearch }">CLEAR</button>
                <button class="btn btn-outline" on:click="{ onSearch }">SEARCH</button>
            </div>
        </div>
    </div>

    <div class="mb-4">
        <div class="row">
            <div class="col-xl-2">
                <select class="form-select form-select-sm" bind:value="{ search.limit }"
                        on:change="{ onSearch }">
                    {#each itemsPerPage as item}
                        <option value="{ item }">display { item } / page</option>
                    {/each}
                </select>
            </div>
        </div>
    </div>

    {#if shifts}
        {#if shifts.length}
            <table class="table table-list table-list-border mb-4">
                <TableHeader {tableColumns} on:sort={ onColumnSort }/>
                <tbody>
                {#each shifts as shift}
                    <tr class="row-hover" class:row-selected="{ selectedId === shift.id }">
                        <td>
                            <button class="btn-reset is-link" on:click="{ () => editShift(shift) }">
                                { formatDate(shift.start_at, 'd-m-y H:i') }
                            </button>
                            <div class="hidden-xl hidden-lg hidden-md text-gray">
                                <div>
                                    {millisecondsToHours(shift.duration)}
                                    {#if shift.total_klm > 0}
                                        {shift.total_klm}Klm
                                    {/if}
                                </div>
                                <div class="d-flex align-items-center justify-content-between">
                                    {#if shift.driver}
                                        <div>{shift.driver.name}</div>
                                    {/if}
                                    {#if shift.vehicle}
                                        <div>{shift.vehicle.licence_no}</div>
                                    {/if}
                                </div>
                            </div>
                        </td>
                        <td class="text-gray hidden-sm hidden-xs">
                            { formatDate(shift.end_at, 'd-m-y H:i') }
                        </td>
                        <td class="text-gray hidden-sm hidden-xs">
                            {#if shift.end_at}
                                {millisecondsToHours(shift.duration)}
                            {/if}
                        </td>
                        <td class="text-gray text-end hidden-sm hidden-xs">
                            {toInt(shift.total_klm, true)}
                        </td>
                        {#if user.isGod()}
                            <td class="text-gray text-end hidden-sm hidden-xs">
                                {shift.total_expenses}
                            </td>
                            <td class="text-gray text-end hidden-sm hidden-xs">
                                {shift.total_incomes}
                            </td>
                        {/if}
                        <td class="text-gray hidden-sm hidden-xs">
                            {#if shift.driver}
                                {shift.driver.name}
                            {/if}
                        </td>
                        <td class="text-gray hidden-sm hidden-xs">
                            {#if shift.vehicle}
                                {shift.vehicle.licence_no}
                            {/if}
                            {#if shift.is_service_day == 1}
                                <span class="d-inline-block ms-2" title="service day">
                                    <AppIcon icon="service" size="12"/>
                                </span>
                            {/if}
                        </td>
                        <td class="button-column p-0">
                            <button class="btn" title="delete shift" on:click={() => deleteShift(shift)}>
                                <AppIcon icon="trash" size="16"/>
                            </button>
                        </td>
                    </tr>
                {/each}
                {#if user.isGod()}
                    <tr>
                        <td colspan="3" class="fw-semibold">Total: {totalShifts}</td>
                        <td class="fw-semibold text-end">{toInt(totalKlm, true)}</td>
                        <td class="fw-semibold text-end">{totalExpenses}</td>
                        <td class="fw-semibold text-end">{totalIncomes}</td>
                        <td colspan="3"></td>
                    </tr>
                {/if}
                </tbody>
            </table>

            <Pagination itemsPerPage="{ search.limit }" totalItems="{ totalShifts }"
                        page="{ page }"
                        on:change="{ onPageChange }"/>
        {:else}
            <div class="panel-simple panel-pad">
                There are no shifts found
            </div>
        {/if}
    {/if}
{/if}

{#if editId != null}
    <ShiftModal shiftId={editId} closeCallback={closeShift}/>
{/if}